import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "configureStore";
import PaginationControls from "components/pagination/PaginationControls";
import {
  fetchMergeHistoryDetailThunk,
  resetDetail,
  setDetailPage,
  setDetailPageSize,
} from "./user-merge-history-container.slice";
import {
  formatMergeHistoryTimestamp,
  formatUser,
} from "./user-merge-history-formatters";

export default function UserMergeHistoryDetailContainer() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId, mergeBatchId } = useParams<{
    companyId: string;
    mergeBatchId: string;
  }>();
  const previousDetailKey = useRef(`${companyId}:${mergeBatchId}`);
  const skipNextResetFetch = useRef(false);

  const { batch, steps, isLoading, errorCode, pagination, page, pageSize } =
    useSelector(
      (state: RootState) => state.mergeHistoryContainerReducer.detail
    );

  useEffect(() => {
    if (!companyId || !mergeBatchId) {
      return;
    }

    const detailKey = `${companyId}:${mergeBatchId}`;
    if (previousDetailKey.current !== detailKey) {
      previousDetailKey.current = detailKey;
      dispatch(resetDetail());
      skipNextResetFetch.current = page !== 1 || pageSize !== 25;
      dispatch(fetchMergeHistoryDetailThunk(companyId, mergeBatchId));
      return;
    }

    if (skipNextResetFetch.current) {
      skipNextResetFetch.current = false;
      return;
    }

    dispatch(fetchMergeHistoryDetailThunk(companyId, mergeBatchId));
  }, [dispatch, companyId, mergeBatchId, page, pageSize]);

  useEffect(() => {
    return () => {
      dispatch(resetDetail());
    };
  }, [dispatch]);

  if (!companyId || !mergeBatchId) {
    return null;
  }

  const totalItems =
    typeof pagination?.total === "number" ? pagination.total : steps.length;
  const totalPages =
    typeof pagination?.totalPages === "number"
      ? pagination.totalPages
      : totalItems > 0
      ? Math.ceil(totalItems / pageSize)
      : 0;
  const hasNextPage =
    typeof pagination?.hasNextPage === "boolean"
      ? pagination.hasNextPage
      : totalPages > 0 && page < totalPages;
  const hasPreviousPage =
    typeof pagination?.hasPreviousPage === "boolean"
      ? pagination.hasPreviousPage
      : page > 1;
  const shouldShowPagination = !isLoading && !errorCode && Boolean(pagination);
  const shouldShowSteps = !isLoading && !errorCode && steps.length > 0;

  return (
    <div className="w-full px-0 py-4 md:px-6">
      <button
        type="button"
        className="text-sm text-blue-600 hover:text-blue-800 mb-4 max-md:-mt-3 max-md:mb-1 max-md:py-3"
        onClick={() => navigate(`/app/${companyId}/user-merge-history`)}
      >
        ← {t("merge_history_back_to_list")}
      </button>

      <h1 className="text-2xl font-semibold text-gray-900 mb-2">
        {t("user_merge_history_detail")}
      </h1>

      {isLoading && (
        <p className="text-gray-500 text-sm">{t("feed_loading")}</p>
      )}

      {!isLoading && errorCode === "not_found" && (
        <p className="text-red-600 text-sm">
          {t("merge_history_batch_not_found")}
        </p>
      )}

      {!isLoading && errorCode === "load_failed" && (
        <p className="text-red-600 text-sm">{t("merge_history_load_error")}</p>
      )}

      {!isLoading && !errorCode && batch && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm space-y-1">
          <div>
            <span className="text-gray-500">
              {t("merge_history_source_user")}:{" "}
            </span>
            <span className="font-medium">{formatUser(batch.sourceUser)}</span>
          </div>
          <div>
            <span className="text-gray-500">
              {t("merge_history_target_user")}:{" "}
            </span>
            <span className="font-medium">{formatUser(batch.targetUser)}</span>
          </div>
          <div>
            <span className="text-gray-500">{t("merge_history_actor")}: </span>
            <span className="font-medium">
              {formatUser(batch.createdByUser)}
            </span>
          </div>
          <div>
            <span className="text-gray-500">
              {t("merge_history_merged_at")}:{" "}
            </span>
            <span>{formatMergeHistoryTimestamp(batch.mergedAt)}</span>
          </div>
        </div>
      )}

      {(shouldShowSteps || shouldShowPagination) && (
        <div className="rounded-lg border border-gray-200">
          {shouldShowSteps && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm max-lg:min-w-[900px]">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="p-2 border-b">
                      {t("merge_history_sequence")}
                    </th>
                    <th className="p-2 border-b">
                      {t("merge_history_table_column")}
                    </th>
                    <th className="p-2 border-b">
                      {t("merge_history_column_column")}
                    </th>
                    <th className="p-2 border-b">
                      {t("merge_history_operation")}
                    </th>
                    <th className="p-2 border-b">
                      {t("merge_history_row_id")}
                    </th>
                    <th className="p-2 border-b w-[28%]">
                      {t("merge_history_old_value")}
                    </th>
                    <th className="p-2 border-b w-[28%]">
                      {t("merge_history_new_value")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {steps.map((s) => (
                    <tr
                      key={s.id}
                      className="border-b border-gray-100 align-top"
                    >
                      <td className="p-2 font-mono text-xs">{s.sequence}</td>
                      <td className="p-2 font-mono text-xs">{s.tableName}</td>
                      <td className="p-2 font-mono text-xs">{s.columnName}</td>
                      <td className="p-2">{s.operation}</td>
                      <td className="p-2 font-mono text-xs break-all">
                        {s.rowId ?? "—"}
                      </td>
                      <td className="p-2 max-w-md">
                        <pre className="whitespace-pre-wrap break-words text-xs font-mono bg-gray-50 rounded p-2 max-h-48 overflow-y-auto">
                          {s.oldValue ?? "—"}
                        </pre>
                      </td>
                      <td className="p-2 max-w-md">
                        <pre className="whitespace-pre-wrap break-words text-xs font-mono bg-gray-50 rounded p-2 max-h-48 overflow-y-auto">
                          {s.newValue ?? "—"}
                        </pre>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {shouldShowPagination && (
            <div className="border-t border-[rgba(0,0,0,0.06)] p-4">
              <PaginationControls
                page={page}
                pageSize={pageSize}
                total={totalItems}
                totalPages={totalPages}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                onPageChange={(nextPage) => dispatch(setDetailPage(nextPage))}
                onPageSizeChange={(nextPageSize) =>
                  dispatch(setDetailPageSize(nextPageSize))
                }
                pageSizeOptions={[10, 25, 50, 100]}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
